import React, { useEffect, useState } from 'react'
import { Button, Col, Row, Text, Title } from '@ix/ix-ui'
import { faFacebookF, faInstagram, faLinkedinIn, faTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons'
import { faLevelUpAlt, faTimes, IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { SPUDInputField } from '../../helpers/record'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { AngledArrowContainer } from './AngledArrowContainer'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { SPUDSiteRecordDetails } from '../../../@types/Site.type'

type SocialFieldTypes = {
  type: 'facebook'|'instagram'|'twitter'|'youtube'|'linkedin',
  icon: IconDefinition,
  label: string,
}

export type SocialType = {
  url: string,
  type: 'facebook'|'instagram'|'twitter'|'youtube'|'linkedin',
}

export type SPUDSocialProps = {
  title: string,
  recordType: string,
  siteValues: SPUDSiteRecordDetails,
  disabled: boolean
}

const SOCIAL_TYPES: Array<SocialFieldTypes> = [
  { type: 'facebook', icon: faFacebookF, label: 'Facebook' },
  { type: 'linkedin', icon: faLinkedinIn, label: 'LinkedIn' },
  { type: 'twitter', icon: faTwitter, label: 'Twitter' },
  { type: 'instagram', icon: faInstagram, label: 'Instagram' },
  { type: 'youtube', icon: faYoutube, label: 'YouTube' },
]

const SPUDSocialCard = styled.div`
  width: 100%;
  border-radius: 5px;
  margin: 10px 0;
  box-shadow: 0 0 6px 3px #eaeaea;
  padding: 1em;
`

const SocialCardHeader = styled.div`
  display: flex;
  justify-content: flex-end
`

function SPUDSocials ({ title, siteValues, recordType, disabled }: SPUDSocialProps): React.ReactElement {
  const { control, getValues } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'social_media',
  })

  const [siteSocials, setSiteSocials] = useState<Array<{
    url: string,
    type: string,
  }>>([])

  let disableAutoFocus = true

  useEffect(() => {
    if (recordType === 'service' && siteValues?.social_media) {
      setSiteSocials(siteValues?.social_media)
    } else {
      disableAutoFocus = false
      setSiteSocials([])
    }
  }, [siteValues?.social_media?.length, recordType])

  const formatSocialLabel = (social: string): string => {
    if (social) {
      if (social === 'linkedin') {
        return 'LinkedIn'
      } else if (social === 'youtube') {
        return 'YouTube'
      } else {
        return social.charAt(0).toUpperCase() + social.slice(1)
      }
    }
    return ''
  }

  return (
    <div>
      <Row>
        <Col direction='row'>
          <Title level={4}>
            {title}
          </Title>
        </Col>
      </Row>
      <Row>
        <Col>
          <Row align='center'>
            <Title level={5} marginTop='0'>Social:</Title>
            <div style={{ margin: '0 10px' }}>
              {SOCIAL_TYPES.map(socialType => (
                <Button
                  key={socialType.label}
                  onClick={() => {
                    append({
                      url: '',
                      type: socialType.type,
                    }, { shouldFocus: false })
                  }}
                  disabled={disabled}
                >
                  <FontAwesomeIcon icon={socialType.icon as IconProp}/> {socialType.label}
                </Button>
              ))}
            </div>
          </Row>
        </Col>
      </Row>
      {fields.map((field, index) => (
        <Row key={field.id} align='center'>
          <Col direction='row'>
            <AngledArrowContainer>
              <FontAwesomeIcon size='2x' rotation={90} icon={faLevelUpAlt as IconProp} color='#A3A3A3'/>
            </AngledArrowContainer>
            <SPUDSocialCard>
              <SocialCardHeader>
                <Button onClick={() => remove(index)} active={!disabled}>
                  <FontAwesomeIcon icon={faTimes as IconProp}/> Remove
                </Button>
              </SocialCardHeader>
              <Row>
                <Col>
                  <Controller
                    name={`social_media.${index}.url`}
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) =>
                      <SPUDInputField
                        id='url'
                        placeholder="Url"
                        type='text'
                        label={<Title level={4}>
                          {formatSocialLabel(getValues(`social_media.${index}.type`))} Url
                        </Title>}
                        fullwidth
                        // to prevent the form scrolling down
                        // when adding a new Service from a site
                        // we need to disable autofocus
                        autoFocus={!disableAutoFocus}
                        {...field}
                      />
                    }
                  />
                  {
                    siteSocials?.[index]?.url &&
                    <Text>
                      Site {formatSocialLabel(siteSocials?.[index]?.type)} url {index + 1}: {siteSocials?.[index]?.url}
                    </Text>
                  }
                </Col>
              </Row>
            </SPUDSocialCard>
          </Col>
        </Row>
      ))}
    </div>
  )
}

export default SPUDSocials
